import type { WealthicaAccount, WealthicaConnection } from '../../types';
import { toast } from 'sonner';
import { PersistenceMetricsTracker } from '../../utils/data-integrity';
import { saveAccountsToDatabase, savePositionsToDatabase } from '../../services/WealthicaPersistenceService';

// Local helper types to avoid leaking store internals
type UnknownRecord = Record<string, unknown>;
type UnknownPosition = Record<string, unknown>;

interface MultiStoreConnection {
  isPersisting?: boolean;
  bearerToken?: string;
  positions?: UnknownPosition[];
  accounts?: WealthicaAccount[];
  connectionData?: WealthicaConnection;
}

interface StoreState {
  connections?: Map<string, MultiStoreConnection>;
  isPersisting?: boolean;
  processingState?: string;
  persistenceError?: string | null;
  bearerToken?: string;
  positions?: UnknownPosition[];
  getConnection?: (institutionId: string) => MultiStoreConnection | null | undefined;
  setPersistingForInstitution?: (institutionId: string, value: boolean) => void;
  setPersistenceErrorForInstitution?: (institutionId: string, error: string | null) => void;
  setProcessingStateForInstitution?: (institutionId: string, state: string) => void;
  processIntegrityResults?: (institutionId: string, integrity: unknown) => void;
}

type SetFn = (partial: Partial<StoreState> | ((state: StoreState) => Partial<StoreState>)) => void;
type GetFn = () => StoreState;

const asRecord = (u: unknown): UnknownRecord => (u && typeof u === 'object' ? (u as UnknownRecord) : {});
const getNumber = (u: unknown): number | undefined => (typeof u === 'number' ? u : undefined);

export async function persistAccountsAndPositionsImpl(
  set: unknown,
  get: unknown,
  userId: string,
  connectionData: WealthicaConnection,
  accounts: WealthicaAccount[],
  forceLocalOrOptions?: boolean | { isRefresh?: boolean; forceLocal?: boolean; toastId?: string | number; suppressSuccessToast?: boolean; mode?: 'both' | 'accounts_only' | 'positions_only' }
): Promise<void> {
  const _set = set as SetFn;
  const _get = get as GetFn;
  const options = (typeof forceLocalOrOptions === 'object' && forceLocalOrOptions !== null)
    ? forceLocalOrOptions
    : undefined;
  const toastId = options?.toastId;
  const suppressSuccessToast = options?.suppressSuccessToast ?? !toastId;
  const mode: 'both' | 'accounts_only' | 'positions_only' = options?.mode ?? 'both';

  // RACE CONDITION GUARD (multi-store aware): check connection-level persisting flag if available
  const state = _get();
  const institutionId = connectionData?.institutionId;
  const isMultiStore = typeof state.getConnection === 'function' && typeof state.setPersistingForInstitution === 'function';
  const connection = isMultiStore && institutionId ? state.getConnection!(institutionId) : null;
  const alreadyPersisting = isMultiStore ? !!connection?.isPersisting : !!state.isPersisting;
  if (alreadyPersisting) {
    console.debug('💾 PERSISTENCE: Already in progress, skipping duplicate call', {
      userId: userId?.substring(0, 8) + '...',
      accountCount: accounts?.length || 0
    });
    return;
  }

  console.debug('💾 PERSISTENCE: Starting persistence', {
    userId: userId?.substring(0, 8) + '...',
    accountCount: accounts?.length || 0,
    isRefresh: options?.isRefresh ?? false,
    hasToastId: !!toastId,
    isMultiStore,
    institutionId,
    mode
  });

  // Record persistence attempt
  PersistenceMetricsTracker.recordAttempt();

  // Set processing flags
  if (isMultiStore && institutionId) {
    state.setPersistingForInstitution?.(institutionId, true);
    state.setPersistenceErrorForInstitution?.(institutionId, null);
    state.setProcessingStateForInstitution?.(institutionId, 'processing');
  } else {
    _set({ isPersisting: true, processingState: 'processing', persistenceError: null });
  }

  try {
    // Resolve bearer token and positions from the correct scope
    let bearerToken: string | undefined;
    let positions: UnknownPosition[] = [];

    if (isMultiStore && institutionId) {
      bearerToken = connection?.bearerToken;
      positions = connection?.positions || [];
    } else {
      bearerToken = state.bearerToken;
      positions = state.positions || [];
    }

    if (!bearerToken) {
      try {
        const { WealthicaAuthService } = await import('../../services/WealthicaAuthService');
        const tokenResult = await WealthicaAuthService.getToken(userId);
        if (tokenResult.success && tokenResult.data) {
          bearerToken = tokenResult.data;
          console.debug('💾 PERSISTENCE: Retrieved bearer token from server for save');
        } else {
          throw new Error(tokenResult.error || 'No bearer token available for database save');
        }
      } catch (tokenErr) {
        throw new Error(tokenErr instanceof Error ? tokenErr.message : 'No bearer token available for database save');
      }
    }

    console.debug('💾 PERSISTENCE: Evaluating persistence plan');
    let accountsSavedCount = 0;
    let positionsSavedCount = 0;
    let accountDetailsSource: unknown = null;

    // Phase 1: save accounts unless positions-only mode
    if (mode !== 'positions_only') {
      console.debug('💾 PERSISTENCE: Calling real database save service for accounts');
      const accountResult = await saveAccountsToDatabase(userId, connectionData, bearerToken, accounts);
      accountDetailsSource = accountResult;

      if (!accountResult.success) {
        const accRec = asRecord(accountResult);
        const detailsRec = asRecord(accRec.details);
        const errorRec = asRecord(accRec.error);
        const statusCode = getNumber(detailsRec.statusCode) ?? getNumber(errorRec.status) ?? getNumber(errorRec.statusCode);
        const message = typeof errorRec.message === 'string' ? errorRec.message : (accRec.error ? String(accRec.error) : '');
        const is400 = String(statusCode) === '400';

        if (is400) {
          try {
            console.warn('💾 PERSISTENCE: Accounts save returned 400. Attempting to save connection and retry once...', {
              institutionId: connectionData?.institutionId,
              statusCode,
              message
            });
            const { WealthicaConnectionService } = await import('../../services/WealthicaConnectionService');
            const cdRec = asRecord(connectionData);
            const loginName = typeof cdRec.loginName === 'string' ? cdRec.loginName : 'unknown';
            await WealthicaConnectionService.saveConnectionStatus({
              userId,
              institutionId: connectionData.institutionId,
              institutionIdentifier: connectionData.institutionIdentifier,
              providerName: connectionData.providerName,
              loginName,
              connectionStatus: 'connected'
            });
            const retryResult = await saveAccountsToDatabase(userId, connectionData, bearerToken, accounts);
            if (!retryResult.success) {
              const rr = asRecord(retryResult);
              throw new Error((typeof rr.error === 'string' ? rr.error : undefined) || 'Failed to save accounts to database (after retry)');
            }
            accountDetailsSource = retryResult;
            console.debug('💾 PERSISTENCE: Accounts save retry succeeded');
          } catch (retryErr) {
            console.error('💾 PERSISTENCE: Accounts save retry failed', {
              institutionId: connectionData?.institutionId,
              error: retryErr instanceof Error ? retryErr.message : String(retryErr)
            });
            throw (retryErr instanceof Error ? retryErr : new Error(String(retryErr)));
          }
        } else {
          const accRec2 = asRecord(accountResult);
          throw new Error((typeof accRec2.error === 'string' ? accRec2.error : undefined) || 'Failed to save accounts to database');
        }
      }
    }

    try {
      if (accountDetailsSource) {
        const src = asRecord(accountDetailsSource);
        const accDetails = asRecord(src.details);
        const accData = asRecord(accDetails.data);
        const savedSnake = getNumber(accDetails.accounts_saved) ?? getNumber(accData.accounts_saved);
        const savedCamel = getNumber(accDetails.savedAccounts) ?? getNumber(accData.savedAccounts);
        accountsSavedCount = (savedSnake ?? savedCamel ?? (accounts?.length || 0));
      }
    } catch {
      accountsSavedCount = accounts?.length || 0;
    }

    try {
      if (accountDetailsSource) {
        const src2 = asRecord(accountDetailsSource);
        const accIntegrity = asRecord(asRecord(src2.details)).integrity_check;
        if (isMultiStore && institutionId && state.processIntegrityResults) {
          state.processIntegrityResults(institutionId, accIntegrity);
        }
      }
    } catch { /* noop */ }

    // Phase 2: save positions unless accounts-only mode
    if (mode !== 'accounts_only' && positions && positions.length > 0) {
      console.debug('💾 PERSISTENCE: Calling real database save service for positions', { positionCount: positions.length });
      const positionResult = await savePositionsToDatabase(userId, connectionData, bearerToken, positions);
      if (!positionResult.success) {
        throw new Error(positionResult.error || 'Failed to save positions to database');
      }
      console.debug('💾 PERSISTENCE: Successfully saved positions to database', {
        positionCount: positions.length,
        userId: userId?.substring(0, 8) + '...'
      });
      try {
        const posRec = asRecord(positionResult);
        const posDetails = asRecord(posRec.details);
        const posData = asRecord(posDetails.data);
        const savedSnake = getNumber(posDetails.positions_saved) ?? getNumber(posData.positions_saved);
        const count = getNumber(posDetails.count) ?? getNumber(posData.count);
        positionsSavedCount = (savedSnake ?? count ?? (positions?.length || 0));
      } catch {
        positionsSavedCount = positions?.length || 0;
      }
      try {
        const posRec2 = asRecord(positionResult);
        const posIntegrity = asRecord(asRecord(posRec2.details)).integrity_check;
        if (isMultiStore && institutionId && state.processIntegrityResults) {
          state.processIntegrityResults(institutionId, posIntegrity);
        }
      } catch { /* noop */ }
    }

    // Record success
    PersistenceMetricsTracker.recordSuccess();

    if (isMultiStore && institutionId) {
      state.setPersistingForInstitution?.(institutionId, false);
      state.setPersistenceErrorForInstitution?.(institutionId, null);
      state.setProcessingStateForInstitution?.(institutionId, 'complete');
    } else {
      _set({ isPersisting: false, processingState: 'complete', persistenceError: null });
    }

    if (!suppressSuccessToast) {
      let title = 'Successfully saved accounts and positions';
      let description = `Accounts: ${accountsSavedCount} • Positions: ${positionsSavedCount}`;
      if (mode === 'accounts_only') {
        title = 'Successfully saved accounts';
        description = `Accounts: ${accountsSavedCount}`;
      } else if (mode === 'positions_only') {
        title = 'Successfully saved positions';
        description = `Positions: ${positionsSavedCount}`;
      }
      toast.success(title, { id: toastId, description, duration: 4000 });
    } else if (toastId) {
      toast.dismiss(toastId);
    }

    try {
      window.dispatchEvent(new CustomEvent('wealthica:persist:completed', {
        detail: { userId, institutionId, accountsSavedCount, positionsSavedCount, timestamp: Date.now() }
      }));
    } catch { /* noop */ }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown persistence error';
    console.error('💾 PERSISTENCE: Failed to save to database', { error: errorMessage });
    PersistenceMetricsTracker.recordFailure();

    if (isMultiStore && connectionData?.institutionId) {
      const institutionId = connectionData.institutionId;
      state.setPersistingForInstitution?.(institutionId, false);
      state.setPersistenceErrorForInstitution?.(institutionId, errorMessage);
      state.setProcessingStateForInstitution?.(institutionId, 'error');
    } else {
      _set({ isPersisting: false, processingState: 'error', persistenceError: errorMessage });
    }

    if (toastId) {
      toast.error('Failed to save accounts/positions', { id: toastId, description: errorMessage });
    }
    throw error;
  }
}
